import { useState, useEffect } from 'react';
import { Save, Share2, Trash2, FileSpreadsheet, FolderOpen, FileCheck } from 'lucide-react';
import ExcelJS from 'exceljs';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

interface EvsViewProps {
    driverName: string;
}

interface ArchiveFile {
    name: string;
    path: string;
    uri: string;
}

export default function EvsView({ driverName }: EvsViewProps) {
    const [history, setHistory] = useState<Record<string, string[]>>({});
    const [archives, setArchives] = useState<ArchiveFile[]>([]);
    const [viewMode, setViewMode] = useState<'current' | 'archives'>('current');

    const getCurrentMonthKey = () => {
        const now = new Date();
        return `evs_history_${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}`;
    };

    useEffect(() => {
        // Migration et chargement du mois en cours
        const currentKey = getCurrentMonthKey();
        const stored = localStorage.getItem(currentKey);
        
        // Si ancien format existe encore, on le migre vers le mois en cours s'il n'y a rien
        const oldStored = localStorage.getItem('evsHistory_v1');
        if (oldStored && !stored) {
            localStorage.setItem(currentKey, oldStored);
            localStorage.removeItem('evsHistory_v1');
            setHistory(JSON.parse(oldStored));
        } else if (stored) {
            setHistory(JSON.parse(stored));
        } else {
            setHistory({});
        }

        // Vérifier les mois passés pour auto-archivage
        checkAndAutoArchive();
        loadArchives();
    }, []);

    const checkAndAutoArchive = async () => {
        if (!Capacitor.isNativePlatform()) return;

        const currentKey = getCurrentMonthKey();
        const keys = Object.keys(localStorage);
        const historyKeys = keys.filter(k => k.startsWith('evs_history_') && k !== currentKey);

        for (const key of historyKeys) {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            if (Object.keys(data).length > 0) {
                console.log(`[EVS] Auto-archivage détecté pour ${key}...`);
                const monthName = key.replace('evs_history_', '').replace('_', '-');
                const success = await handleExport('save', data, `EVS_Archive_${driverName.replace(/\s+/g, '_')}_${monthName}.xlsx`, true);
                if (success) {
                    localStorage.removeItem(key);
                }
            }
        }
    };

    const loadArchives = async () => {
        if (!Capacitor.isNativePlatform()) return;
        try {
            const result = await Filesystem.readdir({
                path: 'Nexus_EVS_Archives',
                directory: Directory.Documents
            });
            
            const files = result.files.map(f => ({
                name: f.name,
                path: `Nexus_EVS_Archives/${f.name}`,
                uri: '' // Sera résolu au besoin
            }));
            setArchives(files);
        } catch (e) {
            // Le dossier n'existe peut-être pas encore
            await Filesystem.mkdir({
                path: 'Nexus_EVS_Archives',
                directory: Directory.Documents,
                recursive: true
            }).catch(() => {});
        }
    };

    const handleExport = async (mode: 'save' | 'share' | 'download' = 'download', dataToExport = history, customFileName?: string, silent = false): Promise<boolean> => {
        if (Object.keys(dataToExport).length === 0) {
            if (!silent) alert("Aucune mission enregistrée pour cette période.");
            return false;
        }

        try {
            const templateUrl = '/template_evs.xlsx';
            const response = await fetch(templateUrl);
            if (!response.ok) throw new Error("Impossible de charger le modèle EVS (.xlsx).");
            const arrayBuffer = await response.arrayBuffer();

            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(arrayBuffer);
            const worksheet = workbook.worksheets[0];
            if (!worksheet) throw new Error("Feuille de calcul introuvable.");

            const cellD3 = worksheet.getCell('D3');
            cellD3.value = driverName;

            const datesKeys = Object.keys(dataToExport).sort();
            const daysMap = ['D', 'L', 'Ma', 'Me', 'J', 'V', 'S'];

            const minDateParts = datesKeys[0].split('-').map(Number);
            const maxDateParts = datesKeys[datesKeys.length - 1].split('-').map(Number);
            const startDate = new Date(minDateParts[0], minDateParts[1] - 1, minDateParts[2]);
            const endDate = new Date(maxDateParts[0], maxDateParts[1] - 1, maxDateParts[2]);

            let currentRow = 5;
            let iterDate = new Date(startDate);
            while (iterDate <= endDate) {
                const year = iterDate.getFullYear();
                const month = String(iterDate.getMonth() + 1).padStart(2, '0');
                const day = String(iterDate.getDate()).padStart(2, '0');
                const dateKey = `${year}-${month}-${day}`;
                
                const formattedDate = `${day}/${month}`;
                const dayStr = daysMap[iterDate.getDay()];
                const trains = dataToExport[dateKey] ? dataToExport[dateKey].join(', ') : "";

                worksheet.getCell(`A${currentRow}`).value = formattedDate;
                worksheet.getCell(`B${currentRow}`).value = dayStr;
                worksheet.getCell(`C${currentRow}`).value = trains;

                currentRow++;
                iterDate.setDate(iterDate.getDate() + 1);
            }

            const currentMonth = new Date().toLocaleString('fr-FR', { month: 'long' });
            const fileName = customFileName || `EVS_${driverName.replace(/\s+/g, '_')}_${currentMonth}.xlsx`;
            const buffer = await workbook.xlsx.writeBuffer();

            if (Capacitor.isNativePlatform()) {
                let binary = '';
                const bytes = new Uint8Array(buffer);
                for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
                const base64Data = btoa(binary);

                if (mode === 'save') {
                    const savePath = silent ? `Nexus_EVS_Archives/${fileName}` : fileName;
                    await Filesystem.writeFile({
                        path: savePath,
                        data: base64Data,
                        directory: Directory.Documents,
                        recursive: true
                    });
                    if (!silent) {
                        alert(`Fichier sauvegardé dans "Documents":\n${fileName}`);
                        loadArchives();
                    }
                    return true;
                } else {
                    const result = await Filesystem.writeFile({ path: fileName, data: base64Data, directory: Directory.Cache });
                    await Share.share({ title: 'Mon Carnet EVS', url: result.uri });
                    return true;
                }
            } else {
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = fileName;
                link.click();
                return true;
            }
        } catch (error: any) {
            console.error("Erreur Export:", error);
            if (!silent) alert(`Erreur creation Excel: ${error.message}`);
            return false;
        }
    };

    const handleShareArchive = async (archive: ArchiveFile) => {
        try {
            const result = await Filesystem.getUri({
                path: archive.path,
                directory: Directory.Documents
            });
            await Share.share({
                title: archive.name,
                url: result.uri
            });
        } catch (e) {
            alert("Erreur lors de l'ouverture de l'archive.");
        }
    };

    const handleClearHistory = () => {
        if (window.confirm("Effacer l'historique du mois en cours ?")) {
            localStorage.removeItem(getCurrentMonthKey());
            setHistory({});
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-[#0f172a] overflow-hidden animate-in fade-in duration-500">
            {/* Header / Tabs */}
            <div className="bg-[#1e293b]/50 border-b border-white/5 p-4 shrink-0 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="bg-emerald-500/20 p-2 rounded-xl border border-emerald-500/30">
                        <FileSpreadsheet className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div className="flex bg-slate-800/60 p-1 rounded-xl gap-1 border border-white/5">
                        <button 
                            onClick={() => setViewMode('current')}
                            className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'current' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-900/40' : 'text-slate-500 hover:text-white'}`}
                        >
                            En cours
                        </button>
                        <button 
                            onClick={() => setViewMode('archives')}
                            className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'archives' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-900/40' : 'text-slate-500 hover:text-white'}`}
                        >
                            Archives ({archives.length})
                        </button>
                    </div>
                </div>
            </div>

            {/* Content area */}
            <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 lg:py-6">
                {viewMode === 'current' ? (
                    <div className="space-y-6">
                        {Object.keys(history).length === 0 ? (
                            <div className="h-40 flex flex-col items-center justify-center text-center opacity-40">
                                <FileSpreadsheet size={32} className="text-slate-600 mb-2" />
                                <p className="text-white font-bold">Mois en cours vide</p>
                            </div>
                        ) : (
                            <div className="grid gap-3">
                                {Object.keys(history).sort().reverse().map((date) => (
                                    <div key={date} className="bg-[#1e293b]/30 border border-white/5 rounded-xl lg:rounded-2xl p-3 lg:p-4 flex justify-between items-center group">
                                        <div className="flex items-center gap-4">
                                            <div className="w-1.5 h-10 bg-emerald-500/40 rounded-full"></div>
                                            <div>
                                                <p className="text-[10px] text-slate-500 font-bold uppercase mb-0.5">Date</p>
                                                <p className="text-white font-mono font-black">{date}</p>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2 justify-end max-w-[60%]">
                                            {history[date].map((train, idx) => (
                                                <span key={idx} className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-lg text-[10px] font-black">
                                                    {train}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {archives.length === 0 ? (
                            <div className="h-40 flex flex-col items-center justify-center text-center opacity-40">
                                <FolderOpen size={32} className="text-slate-600 mb-2" />
                                <p className="text-white font-bold">Aucune archive locale</p>
                                <p className="text-[10px] uppercase font-bold text-slate-500">Ils apparaîtront ici à la fin du mois</p>
                            </div>
                        ) : (
                            <div className="grid gap-3">
                                {archives.map((file, idx) => (
                                    <button 
                                        key={idx} 
                                        onClick={() => handleShareArchive(file)}
                                        className="bg-[#1e293b]/30 border border-white/5 rounded-xl lg:rounded-2xl p-3 lg:p-5 flex items-center justify-between hover:bg-[#1e293b]/50 transition-all text-left group"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="bg-emerald-500/10 p-3 rounded-xl border border-emerald-500/20">
                                                <FileCheck className="text-emerald-500 w-6 h-6" />
                                            </div>
                                            <div>
                                                <p className="text-white font-black uppercase text-sm">{file.name}</p>
                                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1 italic">Stocké sur l'appareil</p>
                                            </div>
                                        </div>
                                        <Share2 size={20} className="text-emerald-500" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer actions */}
            {viewMode === 'current' && (
                <div className="bg-[#1e293b]/80 backdrop-blur-md border-t border-white/5 p-4 lg:p-6 flex items-center justify-between gap-3 lg:gap-4 shrink-0 safe-area-bottom">
                    <button onClick={handleClearHistory} disabled={Object.keys(history).length === 0} className="flex items-center gap-2 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-red-500 hover:bg-red-500/10 rounded-2xl transition-all disabled:opacity-20">
                        <Trash2 size={16} /> Vider le mois
                    </button>
                    <div className="flex gap-3">
                        {Capacitor.isNativePlatform() ? (
                            <button onClick={() => handleExport('save')} disabled={Object.keys(history).length === 0} className="flex items-center gap-3 px-6 py-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-widest rounded-[1.2rem] shadow-[0_10px_30px_rgba(16,185,129,0.3)] transition-all active:scale-95 disabled:opacity-50">
                                <Save size={18} /> Clôturer
                            </button>
                        ) : (
                            <button onClick={() => handleExport()} disabled={Object.keys(history).length === 0} className="flex items-center gap-3 px-10 py-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-widest rounded-[1.2rem] shadow-[0_10px_30px_rgba(16,185,129,0.3)] transition-all active:scale-95 disabled:opacity-50">
                                <Save size={18} /> Télécharger *.xlsx
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
